import React from "react";
import {FormattedDate, FormattedTime, FormattedMessage, FormattedHTMLMessage} from "react-intl";

import {Divider} from "@mui/material";
import {ArrowForward} from "@mui/icons-material";

import {statusChange, priceChange} from "domain/enum/lineItem/lineItemActivityType";
import {getTranslation} from "domain/helpers/translations";
import translateNoteContent from "./translateNoteContent";
import CenterText from "./AutomaticLogs/CenterText";
import {
    black500,
    black5000,
    blueColor, green50, green500,
    greyishBlue500,
    irisBlueColor, red50, red500, successColor,
    terraCottaColor, thunderGrayColor, yellowColor
} from "../../../../../assets/jss/main";

const statusColors = {
    'to-ship':     { bg: 'rgba(0, 172, 193, 0.2)',  color: irisBlueColor },
    'canceled':    { bg: 'rgba(237, 106, 90, 0.2)',  color: terraCottaColor },
    'refunded':    { bg: 'rgba(230, 81, 0, 0.2)',    color: '#ff833a' },
    'returned':    { bg: '#FDDDD2',                  color: '#FF823C' },
    'reserved':    { bg: 'rgba(105, 41, 230, 0.2)',  color: '#976ef5' },
    'pending':     { bg: 'rgba(230, 194, 41, 0.2)',  color: yellowColor },
    'confirmed':   { bg: 'rgba(93, 209, 103, 0.4)',  color: successColor },
    'unavailable': { bg: 'rgba(74, 74, 73, 0.2)',    color: thunderGrayColor },
    'returning':   { bg: '#FFE6C9',                  color: '#FF8E5E' },
    'blocked':     { bg: red50,                   color: red500 },
    'validated':   { bg: green50,                   color: green500 },
};

const styles = {
    card: {
        borderRadius: '8px',
        boxShadow: '0px 4px 22px 0px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        backgroundColor: '#fff',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px',
        whiteSpace: 'nowrap',
    },
    leftCol: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    userName: {
        fontWeight: 600,
        color: blueColor,
        fontSize: '14px',
    },
    email: {
        color: greyishBlue500,
        fontSize: '14px',
    },
    centerText: {
        color: black5000,
        fontSize: '14px',
    },
    orderId: {
        fontWeight: 600,
        color: blueColor,
    },
    rightCol: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '4px',
    },
    dateValue: {
        fontWeight: 600,
        color: black500,
        fontSize: '14px',
    },
    timeValue: {
        color: greyishBlue500,
        fontSize: '14px',
        fontWeight: 400,
    },
    body: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '24px',
        fontSize: '14px',
    },
    activityLabel: {
        color: black5000,
    },
    badgesContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    oldValue: {
        color: greyishBlue500,
        fontSize: '14px',
        textDecoration: 'line-through',
    },
    newValue: {
        color: '#3C81FB',
        fontSize: '14px',
        fontWeight: 600,
    },
    emptyValue: {
        color: greyishBlue500,
        fontSize: '14px',
        fontStyle: 'italic',
    },
    arrow: {
        color: greyishBlue500,
        fontSize: '20px',
    },
    valueChangeBox: {
        background: '#F4F4F6',
        borderRadius: '8px',
        padding: '12px 16px',
        boxShadow: '0px 4px 11px rgba(0,0,0,0.04)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
};

function StatusBadge({status}) {
    const colorDef = statusColors[status] || { bg: '#f0f0f0', color: '#666' };
    return (
        <span style={{
            backgroundColor: colorDef.bg,
            color: colorDef.color,
            padding: '6px 16px',
            borderRadius: '48px',
            fontSize: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            height: '32px',
            display: 'inline-flex',
            alignItems: 'center',
        }}>
            <FormattedMessage id={`picking.order.activity.status.${status}`} />
        </span>
    );
}

function AutomaticLog({item, order}) {
    const isNote = item['@type'] === 'Note';
    const user = isNote ? item.member?.user : item.user;
    const userName = user?.fullName || (user ? `${user.givenName} ${user.familyName}` : '');
    const userEmail = user?.email || '';
    const type = isNote ? item.noteType : item.type;

    function renderBody() {
        if (isNote) {
            return (
                <div style={styles.body}>
                    <span style={styles.activityLabel}>{translateNoteContent(item.content)}</span>
                </div>
            );
        }

        const isShippingChange = item.type === 'LineItemCustomerShippingChange';
        const isStatusChange = item.type === statusChange;
        const isPriceChange = item.type === priceChange;

        let oldStatus = item.oldData;
        let newStatus = item.newData;
        if (['returning-runner', 'returning-retailer'].includes(oldStatus)) oldStatus = 'returning';
        if (['returning-runner', 'returning-retailer'].includes(newStatus)) newStatus = 'returning';

        return (
            <div style={styles.body}>
                <div>
                    {isShippingChange ? (
                        <span style={styles.activityLabel}>
                            <FormattedMessage id={`picking.order.show.table.lineItem.activity.${item.type}.${item.field}`} />
                        </span>
                    ) : (
                        <span style={styles.activityLabel}>
                            <FormattedMessage id={`picking.order.show.table.lineItem.activity.${item.type}`} />
                            {item.lineItem?.name && <>: <b>{item.lineItem.name}</b></>}
                        </span>
                    )}
                    {item.cancellationReason && (
                        <span style={{display: 'block', marginTop: '4px'}}>
                            <FormattedHTMLMessage
                                id="picking.order.show.table.lineItem.activity.cancellationReason"
                                values={{cancellationReason: getTranslation(item.cancellationReason).name}}
                            />
                        </span>
                    )}
                </div>
                {isStatusChange && (
                    <div style={styles.badgesContainer}>
                        <StatusBadge status={oldStatus} />
                        <ArrowForward style={styles.arrow} />
                        <StatusBadge status={newStatus} />
                    </div>
                )}
                {(isPriceChange || isShippingChange) && (item.oldData || item.newData) && (
                    <div style={styles.valueChangeBox}>
                        {item.oldData
                            ? <span style={styles.oldValue}>{item.oldData}</span>
                            : <span style={styles.emptyValue}><FormattedMessage id="picking.order.show.table.lineItem.activity.emptyValue" /></span>}
                        <ArrowForward style={styles.arrow} />
                        {item.newData
                            ? <span style={styles.newValue}>{item.newData}</span>
                            : <span style={styles.emptyValue}><FormattedMessage id="picking.order.show.table.lineItem.activity.emptyValue" /></span>}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div style={styles.card}>
            <div style={styles.header}>
                <div style={styles.leftCol}>
                    <span style={styles.userName}>{userName}</span>
                    <span style={styles.email}>{userEmail}</span>
                </div>
                <span style={styles.centerText}>
                    <CenterText item={item} order={order} userName={userName} isNote={isNote} type={type} />
                </span>
                <div style={styles.rightCol}>
                    <span style={styles.dateValue}>
                        <FormattedDate value={new Date(item.createdAt)} />
                    </span>
                    <span style={styles.timeValue}>
                        <FormattedTime value={new Date(item.createdAt)} />
                    </span>
                </div>
            </div>
            <Divider />
            {renderBody()}
        </div>
    );
}

export default AutomaticLog;
